package com.arena_web.ui.components.feed

import androidx.compose.runtime.*
import com.arena_web.data.dto.CreateUserStoryRequest
import com.arena_web.data.dto.UserStoryDto
import com.arena_web.domain.repositories.UserStoryRepository
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.widgets.GradientBorderBox
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.localization.uploadErrorMessage
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Img
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader
import org.w3c.files.get

// Figma "Create stories" — node 7947:199179
// bg var(--l-s2, #09142d), border 1px rgba(60,121,241,0.45), rounded-26, pt-22 pb-32 px-22, gap-40

private const val MAX_STORY_BYTES = 30 * 1024 * 1024 // 30 MB per Figma

@Composable
fun CreateStoryPopup(
    isOpen: Boolean,
    storyRepository: UserStoryRepository,
    onDismiss: () -> Unit,
    onCreated: (UserStoryDto) -> Unit
) {
    if (!isOpen) return

    val t = LocalAppStrings.current
    var fileBytes by remember { mutableStateOf<ByteArray?>(null) }
    var fileName by remember { mutableStateOf<String?>(null) }
    var contentType by remember { mutableStateOf<String?>(null) }
    var previewUrl by remember { mutableStateOf<String?>(null) }
    var submitting by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    fun reset() {
        fileBytes = null; fileName = null; contentType = null
        previewUrl = null; submitting = false; error = null
    }

    val dismiss: () -> Unit = { reset(); onDismiss() }

    val submit: () -> Unit = submit@{
        val bytes = fileBytes; val name = fileName; val mime = contentType
        if (bytes == null || name == null || mime == null) {
            error = t.feedPickPhotoFirst
            return@submit
        }
        submitting = true
        error = null
        scope.launch {
            val upload = storyRepository.uploadImage(bytes, name, mime)
            val url = upload.getOrNull()?.imageUrl
            if (upload.isFailure || url == null) {
                error = uploadErrorMessage(upload.exceptionOrNull(), t, t.feedUploadFailed)
                submitting = false
                return@launch
            }
            val create = storyRepository.createStory(CreateUserStoryRequest(imageUrl = url))
            val story = create.getOrNull()
            if (create.isFailure || story == null) {
                error = uploadErrorMessage(create.exceptionOrNull(), t, t.feedCreateStoryFailed)
                submitting = false
                return@launch
            }
            onCreated(story)
            reset()
            onDismiss()
        }
    }

    ModalScaffold(onOverlayClick = dismiss, maxWidthPx = 700) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sBorderRadius(26)
                .overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden)
                .styleModifier {
                    // Same surface as the Create Post popup
                    property("background", "var(--l-s2, #09142d)")
                    property("padding", "${s(22)} ${s(22)} ${s(32)} ${s(22)}")
                }
                .sGap(40)
                .onClick { it.stopPropagation() }
        ) {
            // Header section (bordered bottom)
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sGap(16)
                    .sPaddingBottom(16)
                    .styleModifier { property("border-bottom", "1px solid rgba(148, 149, 152, 0)") }
            ) {
                SpanText(
                    t.feedCreateStoriesTitle,
                    modifier = Modifier
                        .sFontSize(24)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(32)
                        .sLetterSpacing(0.24)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
                SpanText(
                    t.feedCreateStoriesSubtitle,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(24)
                        .sLetterSpacing(0.16)
                        .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                )
            }

            // Body: drop zone + action buttons
            Column(
                modifier = Modifier.fillMaxWidth().sGap(32),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                StoryDropZone(
                    previewUrl = previewUrl,
                    onPick = {
                        pickImageFile(MAX_STORY_BYTES, onTooLarge = { error = t.feedFileTooLargeStory }) { bytes, name, mime ->
                            fileBytes = bytes
                            fileName = name
                            contentType = mime
                            previewUrl = createObjectUrl(bytes, mime)
                            error = null
                        }
                    },
                    onClear = {
                        fileBytes = null
                        fileName = null
                        contentType = null
                        previewUrl = null
                        error = null
                    }
                )
                if (error != null) {
                    SpanText(
                        error!!,
                        modifier = Modifier.sFontSize(12).color(AppColors.Error)
                    )
                }
                Row(
                    modifier = Modifier.fillMaxWidth().sGap(12),
                    horizontalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SecondaryPillButton(t.commonCancel, onClick = dismiss)
                    PrimaryPillButton(
                        text = if (submitting) t.feedCreating else t.feedCreateStoriesTitle,
                        enabled = !submitting && fileBytes != null,
                        onClick = submit
                    )
                }
            }
        }
    }
}

@Composable
private fun StoryDropZone(
    previewUrl: String?,
    onPick: () -> Unit,
    onClear: () -> Unit,
) {
    val t = LocalAppStrings.current
    // Figma 7947:199185 — 250×400, dashed 2px #0c1787, rounded-22
    Box(
        modifier = Modifier
            .sWidth(250)
            .sHeight(400)
            .sBorderRadius(22)
            .cursor(Cursor.Pointer)
            .onClick { if (previewUrl == null) onPick() }
            .styleModifier {
                property("position", "relative")
                property("overflow", "hidden")
                if (previewUrl == null) {
                    property("border", "2px dashed #0c1787")
                    property(
                        "background",
                        "linear-gradient(227.53deg, rgba(60, 121, 241, 0.06) 49.48%, rgba(5, 4, 12, 0.06) 105.9%)"
                    )
                } else {
                    property("border", "2px solid #0c1787")
                }
            },
        contentAlignment = Alignment.Center
    ) {
        if (previewUrl == null) {
            Column(
                modifier = Modifier.sGap(10),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Column(
                    modifier = Modifier.sGap(16),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    // 48×48 circle with "+" icon — same gradient-rim button as CreatePostPopup
                    Box(
                        modifier = Modifier
                            .sSize(48)
                            .sBorderRadius(100)
                            .styleModifier {
                                property(
                                    "background",
                                    AppGradients.InteractiveSecondary
                                )
                                property("position", "relative")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Box(
                            modifier = Modifier.fillMaxSize().styleModifier {
                                property("position", "absolute")
                                property("inset", "0")
                                property("border", "0.3px solid transparent")
                                property("border-radius", s(100))
                                property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                property("-webkit-mask-composite", "xor")
                                property("mask-composite", "exclude")
                                property("pointer-events", "none")
                            }
                        )
                        Icon(src = AppIcons.AddSecondary, modifier = Modifier.sSize(20))
                    }
                    SpanText(
                        t.feedDropContentHere,
                        modifier = Modifier
                            .sFontSize(14)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(22)
                            .sLetterSpacing(0.028)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
                SpanText(
                    t.feedMaxStorySize,
                    modifier = Modifier
                        .sFontSize(12)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(12)
                        .sLetterSpacing(0.012)
                        .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                )
            }
        } else {
            Img(
                src = previewUrl,
                attrs = Modifier
                    .fillMaxSize()
                    .sBorderRadius(20)
                    .styleModifier { property("object-fit", "cover") }
                    .toAttrs()
            )
            // Remove button (top-right) — same look as CreatePostPopup's DropSlot
            Box(
                modifier = Modifier
                    .sSize(28)
                    .sBorderRadius(100)
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("position", "absolute")
                        property("top", s(8))
                        property("right", s(8))
                        property("background", "rgba(0, 0, 0, 0.6)")
                        property("border", "0.3px solid var(--arena-border-top)")
                    }
                    .onClick {
                        it.stopPropagation()
                        onClear()
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    "×",
                    modifier = Modifier.sFontSize(16).color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
        }
    }
}

// =============================================================================
// Shared modal helpers
// =============================================================================

@Composable
internal fun ModalScaffold(
    onOverlayClick: () -> Unit,
    maxWidthPx: Int,
    content: @Composable () -> Unit
) {
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .styleModifier {
                property("background", "rgba(0, 0, 0, 0.6)")
                property("backdrop-filter", "blur(4px)")
                property("-webkit-backdrop-filter", "blur(4px)")
            }
            .onClick { onOverlayClick() },
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .sMaxWidth(maxWidthPx)
                .fillMaxWidth()
                .sMargin(16)
                .onClick { it.stopPropagation() }
        ) {
            content()
        }
    }
}

@Composable
internal fun PrimaryPillButton(
    text: String,
    enabled: Boolean = true,
    onClick: () -> Unit
) {
    val sportColors = LocalSportColors.current
    val fill = sportColors.primary.toRgb()
    val fillCss = "rgb(${fill.red}, ${fill.green}, ${fill.blue})"
    Box(
        modifier = Modifier
            .sHeight(44)
            .sBorderRadius(100)
            .cursor(if (enabled) Cursor.Pointer else Cursor.Default)
            .styleModifier {
                property("padding", "${s(13)} ${s(28)}")
                // Solid primary fill in padding-box + ButtonBorderGradientPrimary in border-box
                property(
                    "background",
                    "linear-gradient($fillCss, $fillCss) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("border", "0.3px solid transparent")
                property("opacity", if (enabled) "1" else "0.5")
            }
            .onClick { if (enabled) onClick() },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .sLetterSpacing(0.032)
                .color(AppColors.Light.Background)
        )
    }
}

@Composable
internal fun SecondaryPillButton(
    text: String,
    onClick: () -> Unit
) {
    // Same gradient-border treatment as the "Get this card?" popup's Cancel button.
    // Padding lives on an INNER wrapper so the GradientBorderBox overlay (which uses
    // fillMaxSize) covers the full pill — adding padding on the parent would shrink
    // the overlay to the content box and leave gaps on the sides.
    GradientBorderBox(
        modifier = Modifier
            .sHeight(44)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        borderRadius = 100,
        background = AppGradients.InteractiveSecondary,
    ) {
        Box(
            modifier = Modifier.styleModifier { property("padding", "0 ${s(28)}") },
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                text,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }
    }
}

// =============================================================================
// File pick utilities — shared by story + post popups
// =============================================================================

internal fun pickImageFile(
    maxBytes: Int,
    onTooLarge: () -> Unit,
    onFilePicked: (ByteArray, String, String) -> Unit
) {
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.accept = "image/jpeg,image/png,image/webp"
    input.onchange = { _ ->
        val file = input.files?.get(0)
        if (file != null) {
            if (file.size.toInt() > maxBytes) {
                onTooLarge()
            } else {
                val reader = FileReader()
                reader.onload = {
                    val arrayBuffer = reader.result.unsafeCast<org.khronos.webgl.ArrayBuffer>()
                    val int8Array = org.khronos.webgl.Int8Array(buffer = arrayBuffer)
                    val bytes = int8Array.unsafeCast<ByteArray>()
                    // Downscale/recompress before upload — a raw camera shot on
                    // a weak mobile uplink is what aborts mid-transfer.
                    com.arena_web.utils.compressForUploadThen(bytes, file.name, file.type, onFilePicked)
                }
                reader.readAsArrayBuffer(file)
            }
        }
    }
    input.click()
}

internal fun createObjectUrl(bytes: ByteArray, contentType: String): String {
    val jsArray = bytes.unsafeCast<org.khronos.webgl.Int8Array>()
    val uint8 = org.khronos.webgl.Uint8Array(jsArray.buffer)
    val blob = org.w3c.files.Blob(arrayOf(uint8), org.w3c.files.BlobPropertyBag(contentType))
    return org.w3c.dom.url.URL.createObjectURL(blob)
}

